import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { TabBar } from './components/TabBar.jsx';
import { Header } from './components/Header.jsx';
import { AudioProvider } from './audio/AudioProvider.jsx';
import SearchBar from './components/SearchBar.jsx';
import { BookmarksProvider } from './hooks/useBookmarks.jsx';
import { KhatmProvider } from './hooks/useKhatm.jsx';
import SettingsModal from './components/SettingsModal.jsx';
import { useUiLang, t } from './utils/lang.js';
import { settingsAPI } from './utils/api.js';
import { updateSurahProgress } from './utils/surahProgress.js';
import { ensureSurahNamesFont } from './qcfFontLoader.js';

const HomePage = React.lazy(() => import('./pages/HomePage.jsx'));
const SurahsPage = React.lazy(() => import('./pages/SurahsPage.jsx'));
const JuzPage = React.lazy(() => import('./pages/JuzPage.jsx'));
const HizbPage = React.lazy(() => import('./pages/HizbPage.jsx'));
const PagePage = React.lazy(() => import('./pages/PagePage.jsx'));
const BookmarksPage = React.lazy(() => import('./pages/BookmarksPage.jsx'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage.jsx'));
const KhatmPage = React.lazy(() => import('./pages/KhatmPage.jsx'));
const KhatmDetailsPage = React.lazy(() => import('./pages/KhatmDetailsPage.jsx'));
const ReaderPage = React.lazy(() => import('./pages/ReaderPage.jsx'));
const ReadingProgressPage = React.lazy(() => import('./pages/ReadingProgressPage.jsx'));

function HeaderPicker({ label, items, onSelect }) {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onDoc = (e) => { if (!e.target.closest('.header-picker')) setOpen(false); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);
  return (
    <div className="header-picker">
      <button className="header-picker__btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="header-picker__label">{label}</span>
        <span className="chev">▾</span>
      </button>
      {open && (
        <div className="dropdown" role="listbox">
          {items.map((it) => (
            <button key={it.key || it.id} className="dropdown__item" onClick={() => { setOpen(false); onSelect(it); }}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function pad3(n) { return String(n).padStart(3, '0'); }

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const tg = window?.Telegram?.WebApp;
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const uiLang = useUiLang();

  const matchJuz = location.pathname.match(/^\/juz\/(\d+)/);
  const matchPage = location.pathname.match(/^\/page\/(\d+)/);
  const matchKhatmTask = location.pathname.match(/^\/khatm\/(.+)/);
  const matchHizb = location.pathname.match(/^\/hizb\/(\d+)/);
  const matchSurah = location.pathname.match(/^\/surah\/(\d+)/);
  const activeTab = (new URLSearchParams(location.search).get('t') || 'surahs');

  const title =
    location.pathname === '/surahs' ? '' :
      location.pathname === '/bookmarks' ? t('my_bookmarks') :
        location.pathname === '/settings' ? t('settings') : '';
  const showBack = location.pathname !== '/';

  const historyStack = React.useRef([]);
  const isBack = React.useRef(false);

  React.useEffect(() => {
    try { ensureSurahNamesFont(); } catch {}
  }, []);

  React.useEffect(() => {
    try {
      const saved = sessionStorage.getItem('app:historyStack');
      if (saved) historyStack.current = JSON.parse(saved);
    } catch { }
  }, []);

  React.useEffect(() => {
    const currentPath = location.pathname + location.search;
    const stack = historyStack.current;

    if (isBack.current) {
      isBack.current = false;
      try { sessionStorage.setItem('app:historyStack', JSON.stringify(stack)); } catch { }
      return;
    }

    const isTransient = !!(location && location.state && location.state.transient);
    const isReplace = String(navType || '').toUpperCase() === 'REPLACE';

    if (location.pathname === '/') {
      historyStack.current = ['/'];
    } else {
      if (!isTransient && !isReplace) {
        if (stack[stack.length - 1] !== currentPath) {
          stack.push(currentPath);
        }
      }
    }
    try { sessionStorage.setItem('app:historyStack', JSON.stringify(historyStack.current)); } catch { }
  }, [location.pathname, location.search, location.state, navType]);

  const handleBack = React.useCallback(() => {
    const stack = historyStack.current;
    const path = location.pathname;

    const isTabPage = (p) => (
      p === '/surahs' || p === '/bookmarks' || p === '/settings' || p === '/khatm' || p === '/reading-progress'
    );
    const isReadingPath = (p) => /^\/(surah|juz|hizb|page)\//.test(p);

    if (isTabPage(path)) {
      isBack.current = true;
      historyStack.current = ['/'];
      navigate('/', { replace: true });
      try { sessionStorage.setItem('app:historyStack', JSON.stringify(historyStack.current)); } catch { }
      return;
    }

    if (isReadingPath(path)) {
      let target = '/';
      let newLen = 1;
      for (let i = stack.length - 2; i >= 0; i--) {
        const p = stack[i] || '';
        if (!isReadingPath(p)) { target = p; newLen = i + 1; break; }
      }
      isBack.current = true;
      historyStack.current = stack.slice(0, newLen);
      navigate(target);
      try { sessionStorage.setItem('app:historyStack', JSON.stringify(historyStack.current)); } catch { }
      return;
    }

    if (stack.length > 1) {
      stack.pop();
      const prev = stack[stack.length - 1];
      isBack.current = true;
      navigate(prev);
    } else {
      if (location.pathname !== '/') {
        isBack.current = true;
        navigate('/');
      } else {
        if (tg && tg.close) tg.close();
      }
    }
  }, [navigate, location.pathname, tg, location]);

  React.useEffect(() => {
    if (!tg) return;

    if (tg.BackButton) {
      tg.BackButton.show();

      if (showBack) {
        const handler = () => handleBack();
        tg.onEvent('backButtonClicked', handler);

        if (typeof tg.disableClosingConfirmation === 'function') {
          tg.disableClosingConfirmation();
        }

        return () => {
          tg.offEvent('backButtonClicked', handler);
        };
      } else {
        const handler = () => {
          if (tg.close) tg.close();
        };
        tg.onEvent('backButtonClicked', handler);

        if (typeof tg.enableClosingConfirmation === 'function') {
          tg.enableClosingConfirmation();
        }

        return () => {
          tg.offEvent('backButtonClicked', handler);
        };
      }
    }
  }, [showBack, tg, handleBack, location.pathname]);

  const headerChildren = null;

  const isReadingPage = !!(matchSurah || matchJuz || matchHizb || matchPage);

  const isHomePage = location.pathname === '/';
  const isSurahsListPage = location.pathname === '/surahs';
  const showSettings = true;
  const hideOnScroll = true;

  React.useEffect(() => {
    const onOpenTranslation = () => {
      setSettingsOpen(true);
      setTimeout(() => { try { window.dispatchEvent(new Event('scrollToTranslationSettings')); } catch { } }, 100);
    };
    const onOpenSettings = () => setSettingsOpen(true);
    const onOpenSearch = () => setSearchOpen(true);
    window.addEventListener('openTranslationSettings', onOpenTranslation);
    window.addEventListener('app:openSettings', onOpenSettings);
    window.addEventListener('app:openSearch', onOpenSearch);
    return () => {
      window.removeEventListener('openTranslationSettings', onOpenTranslation);
      window.removeEventListener('app:openSettings', onOpenSettings);
      window.removeEventListener('app:openSearch', onOpenSearch);
    };
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      const detail = e && e.detail;
      const surahId = detail && detail.surahId;
      const ayah = detail && detail.ayah;
      if (!Number.isFinite(surahId) || !Number.isFinite(ayah)) return;
      try {
        updateSurahProgress(surahId, ayah);
      } catch { }
    };
    try {
      window.addEventListener('readingProgressTick', handler);
    } catch { }
    return () => {
      try {
        window.removeEventListener('readingProgressTick', handler);
      } catch { }
    };
  }, []);

  React.useEffect(() => {
    try {
      if (uiLang === 'ar') document.documentElement.setAttribute('dir', 'rtl');
      else document.documentElement.setAttribute('dir', 'ltr');
    } catch { }
  }, [uiLang]);

  React.useEffect(() => {
    (async () => {
      try {
        await settingsAPI.syncFromServer();
        const theme = localStorage.getItem('theme');
        if (theme) {
          document.documentElement.setAttribute('data-theme', theme);
        }
        const qcf = localStorage.getItem('qcfStyle') || localStorage.getItem('font_style');
        if (qcf) {
          try { window.dispatchEvent(new CustomEvent('qcfStyleChanged', { detail: { style: qcf } })); } catch { }
        }
      } catch { }
    })();
  }, []);

  return (
    <KhatmProvider>
      <BookmarksProvider>
        <AudioProvider>
          <div className="app-shell">
            <Header
              title={headerChildren ? title : ''}
              showBack={showBack}
              onBack={handleBack}
              showSettings={showSettings}
              hideOnScroll={hideOnScroll}
              onOpenSettings={() => setSettingsOpen(true)}
              showSearch={true}
              onOpenSearch={() => setSearchOpen(true)}
            >
              {headerChildren}
            </Header>

            {searchOpen && <SearchBar isOpen={searchOpen} onClose={() => setSearchOpen(false)} />}
            <main className="page">
              <Suspense fallback={<div className="stack gap-12 p-4" style={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}><div className="muted">{t('loading')}</div></div>}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/reading-progress" element={<ReadingProgressPage />} />
                  <Route path="/surahs" element={<SurahsPage />} />
                  <Route path="/surah/:id" element={<ReaderPage />} />
                  <Route path="/juz/:id" element={<JuzPage />} />
                  <Route path="/hizb/:id" element={<HizbPage />} />
                  <Route path="/page/:id" element={<PagePage />} />
                  <Route path="/bookmarks" element={<BookmarksPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/khatm" element={<KhatmPage />} />
                  <Route path="/khatm/:id" element={<KhatmDetailsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </main>

            {!isReadingPage && <TabBar />}

            <SettingsModal
              isOpen={settingsOpen}
              onClose={() => setSettingsOpen(false)}
            />
          </div>
        </AudioProvider>
      </BookmarksProvider>
    </KhatmProvider>
  );
}

export function AppEventBridge() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onOpenTranslation = () => {
      const evt = new Event('app:openSettings');
      window.dispatchEvent(evt);
      setTimeout(() => { try { window.dispatchEvent(new Event('scrollToTranslationSettings')); } catch { } }, 100);
    };
    window.addEventListener('openTranslationSettings', onOpenTranslation);
    return () => window.removeEventListener('openTranslationSettings', onOpenTranslation);
  }, []);
  return null;
}
